import { IMkInputField, MkGenericValue } from '@typescript'
import { StepName } from '@account/components/Register/types'
import { loadMfaFactorsWithFallback } from '@utils/mfaUtils'

export interface IRegistrationInputField extends Omit<IMkInputField, 'options'> {
  validationMessage?: string
  optionLabel?: string
  options?: Array<{ label: string; value: string }>
  feedback?: Array<{ text: string; valid: boolean }>
  value: MkGenericValue
  isValid: boolean
  required: boolean
}

export type RegistrationFormData = Record<string, IRegistrationInputField>

export interface RegistrationContext {
  stateToken: string
  activationToken: string
  registrationToken: string
  email?: string
  userName: string
  firstName?: string
  lastName?: string
  createdAt: number
  expiresAt: number
  brand?: string
  mfaFactors?: any[]
  selectedFactor?: any
  mfaRequired?: boolean
  mfaFactorId?: string
  factorProfile?: any
  phone?: string
  mfaEmail?: string
  verifyLink?: string
  resendLink?: string
  isActivationFlow?: boolean
  isMfaActivationFlow?: boolean
}

export interface RegistrationState {
  selectedFactor: any
  mfaFactors: any[]
  registrationMfaEnabled?: boolean
  user: any
  formData: RegistrationFormData
  currentStepName: StepName
  touchedByStep?: Record<string, boolean>
  registrationToken: string | null
  stateToken: string | null
  activationToken: string | null
  isLoading: boolean
  isSubmitted: boolean
  stepValidationErrors: string[]
  error?: { message: string; raw?: Error; voyager?: unknown }
}

const INITIAL_FIELD_STATE: Omit<IRegistrationInputField, 'id' | 'name' | 'label' | 'type'> = {
  value: null,
  isValid: true,
  validationMessage: '',
  required: false,
  errorMessage: '',
  options: [],
}

const createInitialFormData = (): RegistrationState['formData'] => ({
  activationToken: { ...INITIAL_FIELD_STATE, id: 'activationToken', name: 'activationToken', label: 'Activation Token', type: 'text' },
  contractNumber: { ...INITIAL_FIELD_STATE, id: 'contractNumber', name: 'contractNumber', label: 'Contract Number', type: 'text' },
  verificationMethod: { ...INITIAL_FIELD_STATE, id: 'verificationMethod', name: 'verificationMethod', label: 'Verification Method', type: 'radio' },
  registerAsCompany: { ...INITIAL_FIELD_STATE, id: 'registerAsCompany', name: 'registerAsCompany', label: 'Register as Company', type: 'checkbox' },
  ownerNumber: { ...INITIAL_FIELD_STATE, id: 'ownerNumber', name: 'ownerNumber', label: 'Owner Number', type: 'text', required: true },
  firstName: { ...INITIAL_FIELD_STATE, id: 'firstName', name: 'firstName', label: 'First Name', type: 'text', required: true },
  lastName: { ...INITIAL_FIELD_STATE, id: 'lastName', name: 'lastName', label: 'Last Name', type: 'text', required: true },
  postal: { ...INITIAL_FIELD_STATE, id: 'postal', name: 'postal', label: 'Postal Code', type: 'text' },
  phone: { ...INITIAL_FIELD_STATE, id: 'phone', name: 'phone', label: 'Phone', type: 'tel' },
  phoneCountry: { ...INITIAL_FIELD_STATE, id: 'phoneCountry', name: 'phoneCountry', label: 'Phone Country', type: 'select', value: 'USA' },
  email: { ...INITIAL_FIELD_STATE, id: 'email', name: 'email', label: 'Email', type: 'email' },
  userName: {
    ...INITIAL_FIELD_STATE,
    id: 'userName',
    name: 'userName',
    label: 'Username',
    type: 'text',
    feedback: [
      { text: 'Must be 6 characters or longer', valid: false },
      { text: 'Must begin with a letter (uppercase or lowercase)', valid: false },
    ],
  },
  companyName: { ...INITIAL_FIELD_STATE, id: 'companyName', name: 'companyName', label: 'Company Name', type: 'text' },
  mfaFactors: { ...INITIAL_FIELD_STATE, id: 'mfaFactors', name: 'mfaFactors', label: 'MFA Factors', type: 'array' },
  mfaEmail: { ...INITIAL_FIELD_STATE, id: 'mfaEmail', name: 'mfaEmail', label: 'MFA Email', type: 'email' },
  mfaFactorId: { ...INITIAL_FIELD_STATE, id: 'mfaFactorId', name: 'mfaFactorId', label: 'MFA Factor ID', type: 'text' },
  mfaPhoneCode: { ...INITIAL_FIELD_STATE, id: 'mfaPhoneCode', name: 'mfaPhoneCode', label: 'MFA Phone Code', type: 'text' },
  mfaEmailCode: { ...INITIAL_FIELD_STATE, id: 'mfaEmailCode', name: 'mfaEmailCode', label: 'MFA Email Code', type: 'text' },
  // Activation error fields
  activationErrorMessage: { ...INITIAL_FIELD_STATE, id: 'activationErrorMessage', name: 'activationErrorMessage', label: 'Activation Error Message', type: 'text' },
  isActivationFlow: { ...INITIAL_FIELD_STATE, id: 'isActivationFlow', name: 'isActivationFlow', label: 'Is Activation Flow', type: 'checkbox' },
  suffix: {
    ...INITIAL_FIELD_STATE,
    id: 'suffix',
    name: 'suffix',
    label: 'Suffix',
    type: 'select',
    optionLabel: 'value',
    options: [
      { label: 'junior', value: 'Jr.' },
      { label: 'senior', value: 'Sr.' },
      { label: 'second', value: 'II' },
      { label: 'third', value: 'III' },
      { label: 'fourth', value: 'IV' },
      { label: 'fifth', value: 'V' },
      { label: 'sixth', value: 'VI' },
    ],
  },
  verificationCode: { ...INITIAL_FIELD_STATE, id: 'verificationCode', name: 'verificationCode', label: 'Verification Code', type: 'text' },
})

export const createInitialState = (): RegistrationState => ({
  formData: createInitialFormData(),
  currentStepName: StepName.ACCOUNT_LOOKUP,
  registrationToken: null,
  isLoading: false,
  isSubmitted: false,
  stepValidationErrors: [],
  selectedFactor: null,
  registrationMfaEnabled: true,
  mfaFactors: loadMfaFactorsWithFallback(),
  user: null,
  stateToken: null,
  activationToken: null,
})

export enum ERegistrationAction {
  SET_FIELD_VALUE = 'registration/SET_FIELD_VALUE',
  SET_FORM_DATA = 'registration/SET_FORM_DATA',
  SET_FIELD_ERROR = 'registration/SET_FIELD_ERROR',
  SET_CURRENT_STEP = 'registration/SET_CURRENT_STEP',
  SET_LOADING = 'registration/SET_LOADING',
  SET_ERROR = 'registration/SET_ERROR',
  CLEAR_ERROR = 'registration/CLEAR_ERROR',
  SET_TOKEN = 'registration/SET_TOKEN',
  SET_SUBMITTED = 'registration/SET_SUBMITTED',
  SET_VALIDATION_ERRORS = 'registration/SET_VALIDATION_ERRORS',
  SET_TOUCHED = 'registration/SET_TOUCHED',
  SET_VOYAGER_ERROR = 'registration/SET_VOYAGER_ERROR',
  SET_SELECTED_FACTOR = 'registration/SET_SELECTED_FACTOR',
  SET_MFA_FACTORS = 'registration/SET_MFA_FACTORS',
  SET_USER = 'registration/SET_USER',
  SET_MFA_ENABLED = 'registration/SET_MFA_ENABLED',
  RESET_STATE = 'registration/RESET_STATE',
}

interface SetFieldValueAction {
  type: ERegistrationAction.SET_FIELD_VALUE
  payload: {
    fieldName: string
    value: MkGenericValue
    isValid: boolean
    validationMessage: string
  }
}

interface SetFormDataAction {
  type: ERegistrationAction.SET_FORM_DATA
  payload: RegistrationFormData
}

interface SetFieldErrorAction {
  type: ERegistrationAction.SET_FIELD_ERROR
  payload: {
    fieldName: string
    message: string
  }
}

interface SetCurrentStepAction {
  type: ERegistrationAction.SET_CURRENT_STEP
  payload: StepName
}

interface SetLoadingAction {
  type: ERegistrationAction.SET_LOADING
  payload: boolean
}

interface SetErrorAction {
  type: ERegistrationAction.SET_ERROR
  payload: { message: string; raw?: Error; voyager?: unknown }
}

interface ClearErrorAction {
  type: ERegistrationAction.CLEAR_ERROR
}

interface SetTokenAction {
  type: ERegistrationAction.SET_TOKEN
  payload: string | null
}

interface SetSubmittedAction {
  type: ERegistrationAction.SET_SUBMITTED
  payload: boolean
}

interface SetValidationErrorsAction {
  type: ERegistrationAction.SET_VALIDATION_ERRORS
  payload: string[]
}

interface SetTouchedAction {
  type: ERegistrationAction.SET_TOUCHED
  payload: { step: string; touched: boolean }
}

interface SetVoyagerErrorAction {
  type: ERegistrationAction.SET_VOYAGER_ERROR
  payload: unknown
}

interface SetSelectedFactorAction {
  type: ERegistrationAction.SET_SELECTED_FACTOR
  payload: any
}

interface SetMfaFactorsAction {
  type: ERegistrationAction.SET_MFA_FACTORS
  payload: any[]
}

interface SetMfaEnabled {
  type: ERegistrationAction.SET_MFA_ENABLED
  payload: boolean
}

interface SetUserAction {
  type: ERegistrationAction.SET_USER
  payload: any
}

interface ResetStateAction {
  type: ERegistrationAction.RESET_STATE
}

export type RegistrationAction =
  | SetFieldValueAction
  | SetFormDataAction
  | SetFieldErrorAction
  | SetCurrentStepAction
  | SetLoadingAction
  | SetErrorAction
  | ClearErrorAction
  | SetTokenAction
  | SetSubmittedAction
  | SetValidationErrorsAction
  | SetTouchedAction
  | SetVoyagerErrorAction
  | SetSelectedFactorAction
  | SetMfaFactorsAction
  | SetMfaEnabled
  | SetUserAction
  | ResetStateAction

function updateField(formData: RegistrationFormData, fieldName: string, value: MkGenericValue, isValid: boolean, validationMessage: string): RegistrationFormData {
  return {
    ...formData,
    [fieldName]: {
      ...formData[fieldName],
      value,
      isValid,
      validationMessage,
      errorMessage: '',
    },
  }
}

export const registrationReducer = (state: RegistrationState, action: RegistrationAction): RegistrationState => {
  switch (action.type) {
    case ERegistrationAction.SET_FIELD_VALUE:
      return {
        ...state,
        formData: updateField(state.formData, action.payload.fieldName, action.payload.value, action.payload.isValid, action.payload.validationMessage),
      }

    case ERegistrationAction.SET_FORM_DATA:
      return {
        ...state,
        formData: action.payload,
      }

    case ERegistrationAction.SET_FIELD_ERROR:
      return {
        ...state,
        formData: {
          ...state.formData,
          [action.payload.fieldName]: {
            ...state.formData[action.payload.fieldName],
            isValid: false,
            validationMessage: action.payload.message,
            errorMessage: action.payload.message,
          },
        },
      }

    case ERegistrationAction.SET_CURRENT_STEP: {
      const newState = {
        ...state,
        currentStepName: action.payload,
      }
      return newState
    }

    case ERegistrationAction.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      }

    case ERegistrationAction.SET_TOKEN:
      return {
        ...state,
        registrationToken: action.payload,
      }

    case ERegistrationAction.SET_VALIDATION_ERRORS:
      return {
        ...state,
        stepValidationErrors: action.payload,
      }

    case ERegistrationAction.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      }

    case ERegistrationAction.CLEAR_ERROR:
      return {
        ...state,
        error: undefined,
      }

    case ERegistrationAction.SET_VOYAGER_ERROR:
      return {
        ...state,
        error: { ...(state.error || { message: '' }), voyager: action.payload } as any,
      }

    case ERegistrationAction.SET_SUBMITTED:
      return {
        ...state,
        isSubmitted: action.payload,
      }

    case ERegistrationAction.SET_TOUCHED: {
      const next = { ...(state.touchedByStep || {}) }
      next[action.payload.step] = action.payload.touched
      return {
        ...state,
        touchedByStep: next,
      }
    }
    case ERegistrationAction.SET_SELECTED_FACTOR:
      return {
        ...state,
        selectedFactor: action.payload,
      }

    case ERegistrationAction.SET_MFA_FACTORS:
      return {
        ...state,
        mfaFactors: action.payload,
      }
    case ERegistrationAction.SET_MFA_ENABLED:
      return {
        ...state,
        registrationMfaEnabled: action.payload,
      }

    case ERegistrationAction.SET_USER:
      return {
        ...state,
        user: action.payload,
      }

    case ERegistrationAction.RESET_STATE:
      return createInitialState()

    default:
      return { ...state }
  }
}
